<template>
  <div class="file-open-setting">
    <template v-if="readOnly">
      <base-section-title :title="sectionTitle" class="m-b-24" />

      <el-table :data="form.markList" border>
        <el-table-column label="一览表序号" type="index" width="90px">
          <template v-slot:default="{ row }">
            <span>{{ row.orderNum }}</span>
          </template>
        </el-table-column>
        <el-table-column label="开标填写项">
          <template v-slot:default="{ row }">
            <span>{{ row.markName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="填写类型">
          <template v-slot:default="{ row }">
            <span>
              {{ bidMarkInputTypeDict.translate(row.inputType).label }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="单位类型">
          <template v-slot:default="{ row }">
            <div v-if="row.markType === bidMarkTypeDict.BID_TOTAL.value">
              投标报价的单位类型由项目所选的报价方式决定
            </div>
            <span v-else>
              {{ bidMarkUnitTypeDict.translate(row.markUnit).label }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="是否必填">
          <template v-slot:default="{ row }">
            <span>{{ row.markRequire ? '是' : '否' }}</span>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <template v-else>
      <base-section-title :title="sectionTitle" class="m-b-24" />

      <el-form :model="form">
        <el-table :data="form.markList" border>
          <el-table-column label="一览表序号" type="index" width="90px">
            <template v-slot:default="{ $index }">
              <el-form-item>
                <span>{{ $index + 1 }}</span>
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column label="开标填写项">
            <template v-slot:default="{ row, $index }">
              <div v-if="row.isDefault">{{ row.markName }}</div>
              <el-form-item
                v-else
                :prop="'markList.' + $index + '.markName'"
                :rules="rules.markName"
              >
                <el-input
                  v-model.trim="row.markName"
                  maxlength="50"
                  placeholder="请输入"
                />
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column label="填写类型">
            <template v-slot:default="{ row, $index }">
              <div v-if="row.isDefault">
                {{ bidMarkInputTypeDict.translate(row.inputType).label }}
              </div>
              <el-form-item
                v-else
                :prop="'markList.' + $index + '.inputType'"
                :rules="rules.inputType"
              >
                <el-select v-model="row.inputType" placeholder="请选择">
                  <el-option
                    v-for="item in bidMarkInputTypeDict.array"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column label="单位类型">
            <template v-slot:default="{ row, $index }">
              <div v-if="row.markType === bidMarkTypeDict.BID_TOTAL.value">
                投标报价的单位类型由项目所选的报价方式决定
              </div>
              <div v-else-if="row.isDefault">
                {{ bidMarkUnitTypeDict.translate(row.markUnit).label }}
              </div>
              <el-form-item
                v-else
                :prop="'markList.' + $index + '.markUnit'"
                :rules="rules.markUnit"
              >
                <el-select v-model="row.markUnit" placeholder="请选择">
                  <el-option
                    v-for="item in bidMarkUnitTypeDict.array"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column label="是否必填">
            <template v-slot:default="{ row, $index }">
              <div v-if="row.isDefault">
                {{ row.markRequire ? '是' : '否' }}
              </div>
              <el-form-item
                v-else
                :prop="'markList.' + $index + '.markRequire'"
                :rules="rules.markRequire"
              >
                <el-select v-model="row.markRequire" placeholder="请选择">
                  <el-option label="是" :value="true" />
                  <el-option label="否" :value="false" />
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="140px">
            <template v-slot:default="{ $index }">
              <el-button
                v-if="$index !== 0"
                type="text"
                @click="upItem($index)"
              >
                上移
              </el-button>
              <el-button
                v-if="showDownItem($index)"
                type="text"
                @click="downItem($index)"
              >
                下移
              </el-button>
              <el-button
                v-if="showRemoveItem($index)"
                type="text"
                @click="removeItem($index)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
      <div class="add-button" @click="addItem">
        <i class="el-icon-plus"></i>
        新增目录
      </div>
    </template>
  </div>
</template>

<script>
import {
  bidFileTypeDict,
  bidMarkInputTypeDict,
  bidMarkTypeDict,
  bidMarkUnitTypeDict,
  reviewModelDict
} from '@/dictionary/project/kpb-project'
import { whetherBooleanDict } from '@/dictionary/project/common'
import { countRepeatNum, moveInArray } from '@/utils/utils'
import BaseSectionTitle from '@/components/common/BaseSectionTitle.vue'
import { cloneDeep, isEqual } from 'lodash'
import { requiredRule } from '@/utils/form-validate'

/**
 * @typedef OpenMark 开标一览表项
 * @property {number} orderNum 序号
 * @property {string} markName 唱标项名称
 * @property {string} inputType 输入类型
 * @property {string} markUnit 唱标项单位
 * @property {string} markRequire 是否必填
 * @property {string} markType 唱标项类型
 */

/**
 * 获取开标一览表项
 * @param {Object} config
 * @returns OpenMark
 */
function getMark(config) {
  return {
    bidFileType: config.bidFileType,
    orderNum: config.orderNum,
    markName: config.markName,
    inputType: config.inputType,
    markUnit: config.markUnit,
    markRequire: config.markRequire,
    markType: config.markType
  }
}

/**
 * @description 开标一览表设置
 */
export default {
  name: 'FileOpenSetting',

  components: { BaseSectionTitle },

  model: {
    prop: 'list',
    event: 'change'
  },

  props: {
    list: {
      type: Array,
      default: () => []
    },

    readOnly: {
      type: Boolean,
      default: false
    },

    // 评审模式
    reviewModel: {
      type: Number,
      default: reviewModelDict.DEFAULT.value
    },

    // 唱标项所属的文件类型
    bidFileType: {
      type: Number,
      default: bidFileTypeDict.BUSINESS.value
    }
  },

  data() {
    return {
      bidMarkInputTypeDict,
      bidMarkUnitTypeDict,
      whetherBooleanDict,
      bidMarkTypeDict,

      form: {
        /**
         * @type OpenMark[]
         */
        markList: []
      },
      rules: {
        markName: [
          requiredRule('请填写名称'),
          { validator: this.validateName, triggers: 'blur' }
        ],
        inputType: requiredRule('请选择'),
        markUnit: requiredRule('请选择'),
        markRequire: requiredRule('请选择')
      }
    }
  },

  computed: {
    // 双信封模式
    isDouble() {
      return this.reviewModel === reviewModelDict.DOUBLE.value
    },

    sectionTitle() {
      let defaultTitle = '开标一览表'
      // 双信封下，如果bidFileType为商务文件，则显示商务目录
      if (
        this.isDouble &&
        this.bidFileType === bidFileTypeDict.QUOTATION.value
      ) {
        defaultTitle = bidFileTypeDict.QUOTATION.label + '开标一览表'
      }
      if (!this.readOnly) {
        defaultTitle += '设置'
      }
      return defaultTitle
    }
  },

  watch: {
    list: {
      handler(val) {
        if (val?.length) {
          this.form.markList = cloneDeep(val).map(item => {
            let isDefault = false
            if (
              item.markType === bidMarkTypeDict.BID_PERSON.value ||
              item.markType === bidMarkTypeDict.BID_TOTAL.value ||
              item.markType === bidMarkTypeDict.BID_GUARANTEE.value
            ) {
              isDefault = true
            }
            const inputType = item.inputType || ''
            return Object.assign({}, item, { isDefault, inputType })
          })
        }
      },
      deep: true,
      immediate: true
    },

    'form.markList': {
      handler(val) {
        if (val && !isEqual(val, this.list)) {
          const tempList = cloneDeep(val).map((item, index) =>
            Object.assign({}, item, { orderNum: index + 1 })
          )
          this.$emit('change', tempList)
        }
      },
      deep: true
    },

    reviewModel() {
      if (!this.readOnly) {
        this.initMarkList()
      }
    }
  },

  created() {
    if (this.list.length === 0) this.initMarkList()
  },

  methods: {
    initMarkList() {
      this.form.markList = []
      const bidderMark = getMark({
        bidFileType: this.bidFileType,
        markName: '投标单位',
        inputType: bidMarkInputTypeDict.STRING.value,
        markUnit: bidMarkUnitTypeDict.WITHOUT.value,
        markRequire: whetherBooleanDict.YES.value,
        markType: bidMarkTypeDict.BID_PERSON.value,
        isDefault: true
      })
      const quoteMark = getMark({
        bidFileType: this.bidFileType,
        markName: '投标报价',
        inputType: bidMarkInputTypeDict.NUMBER.value,
        markUnit: '',
        markRequire: whetherBooleanDict.YES.value,
        markType: bidMarkTypeDict.BID_TOTAL.value,
        isDefault: true
      })
      // 根据8-2招标电子化迭代需求中的要求，在创建该保证金项时，不对保证金做任何特殊处理，同添加项逻辑相同，供应商需手动填写对应的字段内容
      const guaranteeMark = getMark({
        bidFileType: this.bidFileType,
        markName: '投标保证金',
        inputType: bidMarkInputTypeDict.NUMBER.value,
        markUnit: bidMarkUnitTypeDict.YUAN.value,
        markRequire: whetherBooleanDict.YES.value,
        markType: bidMarkTypeDict.CUSTOMIZE.value
      })
      if (this.reviewModel === reviewModelDict.DEFAULT.value) {
        // 开标一览表默认有三条数据
        const tempMarkList = [bidderMark, quoteMark, guaranteeMark].map(
          (item, index) => {
            item.orderNum = index + 1
            return item
          }
        )
        this.form.markList = tempMarkList
      }
      if (this.reviewModel === reviewModelDict.DOUBLE.value) {
        // 双信封商务开标一览表设置
        if (this.bidFileType === bidFileTypeDict.BUSINESS.value) {
          const tempMarkList = [bidderMark, guaranteeMark].map(
            (item, index) => {
              item.orderNum = index + 1
              return item
            }
          )
          this.form.markList = tempMarkList
        }
        // 双信封报价开标一览表设置
        if (this.bidFileType === bidFileTypeDict.QUOTATION.value) {
          const tempMarkList = [bidderMark, quoteMark].map((item, index) => {
            item.orderNum = index + 1
            return item
          })
          this.form.markList = tempMarkList
        }
      }
    },

    addItem() {
      const markItem = getMark({
        bidFileType: this.bidFileType,
        orderNum: this.form.markList.length + 1,
        markType: bidMarkTypeDict.CUSTOMIZE.value
      })
      this.form.markList.push(markItem)
    },

    upItem(index) {
      moveInArray(this.form.markList, index, index - 1)
      this.form.markList.forEach((item, index) => {
        item.orderNum = index + 1
      })
    },

    showDownItem(index) {
      if (this.form.markList.length === 1) {
        return false
      }
      return index !== this.form.markList.length - 1
    },

    downItem(index) {
      moveInArray(this.form.markList, index, index + 1)
      this.form.markList.forEach((item, index) => {
        item.orderNum = index + 1
      })
    },

    showRemoveItem(index) {
      // 唱标项类型为自定义的可以删除
      const mark = this.form.markList[index]
      return mark.markType === bidMarkTypeDict.CUSTOMIZE.value
    },

    removeItem(index) {
      this.form.markList.splice(index, 1)
    },

    validateName(rule, value, callback) {
      const repeatObj = countRepeatNum(this.form.markList, 'markName')
      if (repeatObj[value].num > 1) {
        callback(new Error('当前名称已存在'))
        return
      }
      callback()
    }
  }
}
</script>

<style scoped lang="scss">
.file-open-setting {
  background: $white-color;
  border-radius: 4px;
  padding: 20px;
}

.add-button {
  width: 100%;
  padding: 4px 0;
  border: 1px dashed $border-color;
  border-radius: 4px;
  text-align: center;
  margin-top: 16px;
  cursor: pointer;

  &:hover {
    border-color: $primary-color;
    color: $primary-color;
  }
}

::v-deep .el-form-item {
  margin-bottom: 26px;
  margin-top: 26px;
}
</style>
